/**
 * Status of the public/shareable link for a page.
 * Mirrors the GraphQL PublicLinkPageStatus enum as a runtime value.
 */
export enum PublicLinkPageStatus {
	BLOCKED_BY_ORG = 'BLOCKED_BY_ORG',
	BLOCKED_BY_ORG_DEFAULT_POLICY = 'BLOCKED_BY_ORG_DEFAULT_POLICY',
	BLOCKED_BY_PRODUCT = 'BLOCKED_BY_PRODUCT',
	BLOCKED_BY_SPACE = 'BLOCKED_BY_SPACE',
	BLOCKED_BY_CONTAINER_POLICY = 'BLOCKED_BY_CONTAINER_POLICY',
	BLOCKED_BY_CLASSIFICATION_LEVEL = 'BLOCKED_BY_CLASSIFICATION_LEVEL',
	OFF = 'OFF',
	ON = 'ON',
	SITE_BLOCKED = 'SITE_BLOCKED',
	SITE_DISABLED = 'SITE_DISABLED',
	SPACE_BLOCKED = 'SPACE_BLOCKED',
	SPACE_DISABLED = 'SPACE_DISABLED',
	TRASHED = 'TRASHED',
}

/**
 * The access scope of a shareable link.
 * - `INTERNAL_LICENSED`: Workspace link — accessible to licensed users only
 * - `EXTERNAL`: Public link — accessible to anyone with the URL
 */
export type ConfluenceShareableLinkAccessType = 'INTERNAL_LICENSED' | 'EXTERNAL';

export type RefetchType = 'all' | 'permission' | 'public-link';

/**
 * Computed shareable link state for a page, combining data from the page query,
 * content query, and permissions query.
 */
export type ShareableLinkPageDataCalculated = {
	/** Are shareable links enabled by admins at site and space levels */
	isShareableLinkAdminEnabled: boolean;
	/** Is shareable link allowed for content type, admin enabled, and status ON */
	isShareableLinkFullyEnabled: boolean;
	/** Are shareable links allowed for this content type */
	isShareableLinkAllowedForContentType: boolean;
	/** Are editable shareable links allowed for this content type */
	isShareableLinkEditAllowedForContentType: boolean;
	/** Does the shareable link itself have edit permissions (e.g. editable public whiteboards) */
	isEditShareableLink: boolean;
	/** Can current user edit shareable link status for this page */
	canEditShareableLink: boolean;
	shareableLinkStatus?: PublicLinkPageStatus | null;
	shareableLinkSharePath?: string | null;
	/** The access scope of the shareable link (INTERNAL_LICENSED, EXTERNAL, or null if no link) */
	accessType?: ConfluenceShareableLinkAccessType | null;
	refetch: (dataToFetch?: RefetchType) => void;
};

/**
 * Common data always present in the shareable link page provider response,
 * regardless of loading state.
 */
interface ShareableLinkPageProviderCommonData {
	errors: Error[];
}

/**
 * Discriminated union returned by useShareableLinkPageDataProvider.
 * When `isLoading` is true, only `errors` is available.
 * When `isLoading` is false, the full computed state is available.
 */
export type ShareableLinkPageProviderData = ShareableLinkPageProviderCommonData &
	(
		| { isLoading: true }
		| ({ isLoading: false } & ShareableLinkPageDataCalculated)
	);
